import React, { useState } from "react";
import { useIntl } from "react-intl";
import { DictType, SystemEntity } from "@/model";
import { Toggle } from "@/component/input";
import Tooltip from "@/component/Tooltip";


const SYSTEM_ENTITY_DESCRIPTIONS = [
    { key: "sys.time", name: "i13009", description: "i13019", example: "i13019_1" },
    { key: "sys.date", name: "i13010", description: "i13020", example: "i13020_1" },
    { key: "sys.currency.krw", name: "i13011", description: "i13021", example: "i13021_1" },
    { key: "sys.unit.length", name: "i13012", description: "i13022", example: "i13022_1" },
    { key: "sys.phone.number", name: "i13013", description: "i13023", example: "i13023_1" },
    { key: "sys.number", name: "i13014", description: "i13024", example: "i13024_1" },
    { key: "sys.stock.quantity", name: "i13015", description: "i13025", example: "i13025_1" }
] as const;
const SYSTEM_ENTITY_KEYS = SYSTEM_ENTITY_DESCRIPTIONS.map(entity => entity.key);

interface SystemEntityTogglesProps {
    defaultSystemEntity: DictType<SystemEntity>;
    onChange: (value: DictType<SystemEntity>) => void;
}

const SystemEntityToggles: React.FC<SystemEntityTogglesProps> = (props) => {
    const { defaultSystemEntity, onChange } = props;

    const intl = useIntl();

    const [systemEntity, setSystemEntity] = useState<DictType<SystemEntity>>(
        Object.fromEntries(
            SYSTEM_ENTITY_KEYS
                .filter(key => defaultSystemEntity[key])
                .map(key => [key, defaultSystemEntity[key]])
        )
    );

    const onToggle = (key: string, type: "enabled" | "post", toggle: boolean) => {
        setSystemEntity(prevState => {
            const newEntities = {
                ...prevState,
                [key]: {
                    enabled: prevState[key]?.enabled ?? false,
                    post: prevState[key]?.post ?? false,
                    [type]: toggle
                }
            };
            onChange(newEntities);
            return newEntities;
        });
    };

    return (
        <div className="flex flex-col gap-2">
            <div className="input-group-heading">
                {intl.formatMessage({ id: "i13005" })}
            </div>
            <div role="table"
                 className="rounded-[6px] shadow-table shadow-black-200 bg-white"
                 data-testid="system-entity-table"
            >
                <div role="rowgroup">
                    <div role="row"
                         className="h-12 grid grid-cols-[1fr_136px_136px] items-center border-b border-black-200"
                    >
                        <div role="columnheader"
                             className="px-[25px] text-black-500 font-[600] text-[14px]"
                        >
                            {intl.formatMessage({ id: "i13006" })}
                        </div>
                        <div role="columnheader"
                             className="px-[15px] text-black-500 font-[600] text-[14px]"
                        >
                            {intl.formatMessage({ id: "i13007" })}
                        </div>
                        <div role="columnheader"
                             className="px-[15px] text-black-500 font-[600] text-[14px]"
                        >
                            {intl.formatMessage({ id: "i13008" })}
                        </div>
                    </div>
                </div>
                <div role="rowgroup">
                    {SYSTEM_ENTITY_DESCRIPTIONS.map((item) => (
                        <div key={item.key}
                             role="row"
                             className="h-12 grid grid-cols-[1fr_136px_136px] border-b border-black-150 last:border-b-0"
                        >
                            <div role="cell"
                                 className="px-2.5 flex items-center gap-1.5"
                            >
                                <div className="pl-[15px] text-black-500 text-[14px] font-[400]">
                                    {intl.formatMessage({ id: item.name })}
                                </div>
                                <Tooltip message={
                                    <div className="flex flex-col">
                                        <div className="font-[600]">
                                            {intl.formatMessage({ id: item.name })}
                                        </div>
                                        <div>
                                            {intl.formatMessage({ id: item.description })}
                                        </div>
                                        <div className="text-black-300">
                                            {intl.formatMessage({ id: item.example })}
                                        </div>
                                    </div>
                                }
                                />
                            </div>
                            <div role="cell"
                                 className="px-[15px] flex items-center"
                            >
                                <Toggle defaultStatus={systemEntity[item.key]?.enabled ?? false}
                                       onToggle={checked => onToggle(item.key, 'enabled', checked)}
                                />
                            </div>
                            <div role="cell"
                                 className="px-[15px] flex items-center"
                            >
                                <Toggle disabled={!systemEntity[item.key]?.enabled}
                                        defaultStatus={systemEntity[item.key]?.post ?? false}
                                        onToggle={checked => onToggle(item.key, 'post', checked)}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default SystemEntityToggles;
